import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Monitor, Smartphone, Tv } from 'lucide-react';

const DISMISS_KEY = 'nls_install_dismissed';
const DISMISS_DAYS = 7;

const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/firetv|aftm|aftb|aftt|afts|aft.*build/i.test(ua)) return 'firetv';
  if (/smart-?tv|hbbtv|netcast|viera|nettv|roku|dlnadoc|ce-html|tv\s*browser|tizen|webos/i.test(ua)) return 'smarttv';
  if (/ipad|tablet|playbook|silk/i.test(ua) || (navigator.maxTouchPoints > 1 && /mac/i.test(ua))) return 'tablet';
  if (/iphone|ipod|android.*mobile|windows phone/i.test(ua)) return 'mobile';
  return 'desktop';
};

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');
  const location = useLocation();
  const isWatchPage = location.pathname.startsWith('/watch/');
  const isAdminPage = location.pathname.startsWith('/admin');

  // Check if dismissed recently
  const isDismissed = useCallback(() => {
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (!dismissed) return false;
      const dismissedDate = new Date(parseInt(dismissed));
      const now = new Date();
      const daysSince = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      return daysSince < DISMISS_DAYS;
    } catch { return false; }
  }, []);

  useEffect(() => {
    if (isStandalone() || isDismissed() || isAdminPage) return;
    setDeviceType(getDeviceType());

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS/devices that don't fire beforeinstallprompt, show after delay
    const timer = setTimeout(() => {
      if (!deferredPrompt && !isStandalone() && !isDismissed()) {
        setVisible(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, [isDismissed, isAdminPage]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch {}
  };

  if (!visible || isStandalone() || isAdminPage) return null;

  const DeviceIcon = deviceType === 'mobile' || deviceType === 'tablet' ? Smartphone
    : deviceType === 'firetv' || deviceType === 'smarttv' ? Tv
    : Monitor;

  const getMessage = () => {
    if (deviceType === 'firetv') return 'Add NLS to your Fire Stick home screen';
    if (deviceType === 'smarttv') return 'Add NLS to your TV home screen';
    if (deviceType === 'mobile' || deviceType === 'tablet') return 'Add NLS to your home screen';
    return 'Install NLS for quick access';
  };

  const getAction = () => {
    // Native install prompt available (Chrome/Edge/Android)
    if (deferredPrompt) {
      return (
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 bg-white text-gray-950 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap"
          data-testid="install-btn"
        >
          <Download className="w-3.5 h-3.5" />
          Install
        </button>
      );
    }

    // iOS Safari instructions
    if (isIOS()) {
      return (
        <span className="text-sky-300 text-xs whitespace-nowrap">
          Tap <span className="inline-block px-1 py-0.5 bg-gray-700 rounded text-[10px] mx-0.5">Share</span> then <span className="inline-block px-1 py-0.5 bg-gray-700 rounded text-[10px] mx-0.5">Add to Home Screen</span>
        </span>
      );
    }

    // Fire TV / Smart TV
    if (deviceType === 'firetv' || deviceType === 'smarttv') {
      return (
        <span className="text-sky-300 text-xs whitespace-nowrap">
          Bookmark this page for quick access
        </span>
      );
    }

    // Desktop fallback
    return (
      <span className="text-sky-300 text-xs whitespace-nowrap">
        Use your browser menu to install this app
      </span>
    );
  };

  // Watch page: small floating pill at bottom-left (doesn't block fullscreen)
  if (isWatchPage) {
    return (
      <div
        className="fixed bottom-4 left-4 z-40 flex items-center gap-2 bg-gray-900/95 backdrop-blur border border-gray-700/60 rounded-full px-4 py-2 shadow-xl animate-in slide-in-from-bottom-4"
        data-testid="install-prompt-watch"
      >
        <DeviceIcon className="w-4 h-4 text-sky-400 flex-shrink-0" />
        <span className="text-gray-200 text-xs font-medium hidden sm:inline">{getMessage()}</span>
        {getAction()}
        <button
          onClick={handleDismiss}
          className="text-gray-500 hover:text-white ml-1 flex-shrink-0"
          data-testid="install-dismiss-watch"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // All other pages: top banner below nav
  return (
    <div
      className="w-full bg-gradient-to-r from-gray-900 via-sky-950/40 to-gray-900 border-b border-sky-800/30"
      data-testid="install-prompt-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 sm:gap-4">
        <DeviceIcon className="w-4 h-4 text-sky-400 flex-shrink-0" />
        <span className="text-gray-200 text-sm font-medium">{getMessage()}</span>
        {getAction()}
        <button
          onClick={handleDismiss}
          className="text-gray-500 hover:text-white ml-1 flex-shrink-0"
          data-testid="install-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
